<template>
  <div class="card p-2.5">
    <div class="flex justify-between items-center mb-2">
      <h2 class="text-lg font-bold">Ticket Categories</h2>
      <Button label="New Category" icon="pi pi-plus" size="small" @click="openCreateModal" data-testid="btn-settings-ticket-cat-new" />
    </div>

    <DataTable :value="categories" :loading="loading" size="small" tableStyle="min-width: 50rem">
      <Column field="name" header="Name"></Column>
      <Column field="priority" header="Default Priority">
        <template #body="slotProps">
           <TicketPriorityBadge :priority="slotProps.data.priority" />
        </template>
      </Column>
      <Column field="auto_response" header="Auto Response">
        <template #body="slotProps">
          <span class="truncate block max-w-xs" :title="slotProps.data.auto_response">
            {{ slotProps.data.auto_response || '—' }}
          </span>
        </template>
      </Column>
      <Column header="Actions">
        <template #body="slotProps">
          <Button icon="pi pi-pencil" text rounded size="small" aria-label="Edit" @click="openEditModal(slotProps.data)" data-testid="btn-settings-ticket-cat-edit" />
        </template>
      </Column>
    </DataTable>

    <!-- Create/Edit Modal -->
    <Dialog v-model:visible="modalVisible" :header="isEditMode ? 'Edit Category' : 'New Category'" modal>
        <div class="flex flex-col gap-2 w-[400px]">
            <div class="flex flex-col gap-1.5">
                <label for="name" class="text-xs font-semibold">Name <span class="text-red-500">*</span></label>
                <InputText id="name" v-model="form.name" :invalid="!!fieldError('name')" data-testid="ticket-cat-name"
                    @blur="touch('name')" />
                <small v-if="fieldError('name')" class="text-red-500" data-testid="ticket-cat-name-error">{{ fieldError('name') }}</small>
            </div>
            <div class="flex flex-col gap-1.5">
                <label for="priority" class="text-xs font-semibold">Default Priority</label>
                <Select id="priority" v-model="form.priority" :options="priorities" optionLabel="label" optionValue="value" placeholder="Select Priority" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label for="auto_response" class="text-xs font-semibold">Auto Response</label>
                <Textarea id="auto_response" v-model="form.auto_response" rows="3" placeholder="Message sent automatically when a ticket is created in this category." />
                <small v-if="fieldError('auto_response')" class="text-red-500">{{ fieldError('auto_response') }}</small>
                <small class="text-gray-500">Optional. Leave empty for no auto-response.</small>
            </div>
        </div>
        <template #footer>
            <Button label="Cancel" icon="pi pi-times" text size="small" @click="modalVisible = false" data-testid="btn-settings-ticket-cat-cancel" />
            <Button label="Save" icon="pi pi-check" size="small" @click="onSubmit" :loading="saving" data-testid="btn-settings-ticket-cat-save" />
        </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useTicketQueries } from '@/composables/queries/useTicketQueries';
import { useTicketMutations } from '@/composables/mutations/useTicketMutations';
import { TicketPriority, type TicketCategory } from '@/types/custom-ticket-types';
import TicketPriorityBadge from '@/components/common/TicketPriorityBadge.vue';
import { useToast } from 'primevue/usetoast';
import { helpers, maxLength, required } from '@vuelidate/validators';
import { useValidatedForm } from '@/composables/useValidatedForm';
import { getErrorMessage } from '@/utils/error';

const { useGetCategories } = useTicketQueries();
const { categories, loading, refetch } = useGetCategories();

const { createCategory, creatingCategory, updateCategory, updatingCategory } = useTicketMutations();
const toast = useToast();

const modalVisible = ref(false);
const isEditMode = ref(false);
const saving = ref(false);

const form = reactive({
  id: '',
  name: '',
  priority: TicketPriority.Medium,
  auto_response: '',
});

// Mirrors CreateTicketCategoryInput: name is required, up to 255 characters.
const { fieldError, touch, validate, reset: resetValidation, handleServerErrors, serverErrors } = useValidatedForm({
  name: {
    required: helpers.withMessage('Enter a category name.', required),
    maxLength: helpers.withMessage('Keep the name to 255 characters or fewer.', maxLength(255)),
  },
}, form);

const priorities = [
  { label: 'Low', value: TicketPriority.Low },
  { label: 'Medium', value: TicketPriority.Medium },
  { label: 'High', value: TicketPriority.High },
  { label: 'Urgent', value: TicketPriority.Urgent },
];

const openCreateModal = () => {
  isEditMode.value = false;
  form.id = '';
  form.name = '';
  form.priority = TicketPriority.Medium;
  form.auto_response = '';
  resetValidation();
  modalVisible.value = true;
};

const openEditModal = (category: TicketCategory) => {
  isEditMode.value = true;
  form.id = category.id;
  form.name = category.name;
  form.priority = category.priority || TicketPriority.Medium;
  form.auto_response = category.auto_response || '';
  resetValidation();
  modalVisible.value = true;
};

const onSubmit = async() => {
  form.name = form.name.trim();
  if (!(await validate())) return;

  saving.value = true;
  try {
    if (isEditMode.value) {
      await updateCategory({
        id: form.id,
        name: form.name,
        priority: form.priority,
        auto_response: form.auto_response,
      });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Category updated successfully.', life: 3000 });
    } else {
      await createCategory({
        input: {
          name: form.name,
          priority: form.priority,
          auto_response: form.auto_response,
        },
      });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Category created successfully.', life: 3000 });
    }
    await refetch();
    modalVisible.value = false;
  } catch (error) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(error, 'Failed to save category.'), life: 5000 });
    }
  } finally {
    saving.value = false;
  }
};
</script>
